import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { useBookingStore } from '@/stores/bookingStore'
import { useMyTravelStore } from '@/stores/myTravelStore'
import { useJourneyStore } from '@/stores/journeyStore'
import { fetchBookingData } from '@/services/journeyService'

export function useRebookingFlow() {
  const route = useRoute()
  const bookingStore = useBookingStore()
  const myTravelStore = useMyTravelStore()

  const rebookingParams = computed(() => ({
    direction: route.query.rebookingType as
      | 'outbound'
      | 'inbound'
      | 'entire',
    bookingNumber: route.query.bookingId as string,
    itemsToReplace: ((route.query.itemsToReplace as string) || '')
      .split(',')
      .filter(Boolean),
  }))

  const bookingId = computed(() => route.query.bookingId as string)
  const rebookingType = computed(() => route.query.rebookingType as string)

  const itemsToReplace = computed(() => {
    if (route.query.itemsToReplace) {
      const items =
        typeof route.query.itemsToReplace === 'string'
          ? [route.query.itemsToReplace]
          : Array.isArray(route.query.itemsToReplace)
          ? route.query.itemsToReplace
          : []
      return items.filter((item): item is string => item !== null)
    }

    const outboundProducts =
      bookingStore.bookingData?.outbound_booking_tariff_segments?.[0]
        ?.required_products || []
    const inboundProducts =
      bookingStore.bookingData?.inbound_booking_tariff_segments?.[0]
        ?.required_products || []

    return [...outboundProducts, ...inboundProducts]
      .map((product) => product.uuid)
      .filter((uuid): uuid is string => !!uuid)
  })

  // Initialize rebooking flow with booking data
  const initializeRebookingFlow = async (bookingId: string, rebookingType: string, entireJourney: boolean = false) => {
    const journeyStore = useJourneyStore()
    const myTravelStore = useMyTravelStore()
    const bookingStore = useBookingStore()
    
    myTravelStore.setRebookingMode(true)
    myTravelStore.setRebookingType(rebookingType as 'outbound' | 'inbound' | 'entire' | null)
    
    if (!bookingStore.bookingData || bookingStore.bookingData.booking_number !== bookingId) {
      try {
        await fetchBookingData(bookingId, true)
      } catch (error) {
        console.error('Error fetching booking data for rebooking:', error)
      }
    }
    
    if (bookingStore.bookingData && bookingStore.bookingData.total_price_paid) {
      myTravelStore.setOriginalBookingPrice(bookingStore.bookingData.total_price_paid)
    }
    
    // Extract dates from booking data for the appropriate journey direction
    let bookingOutboundDate = null;
    let bookingReturnDate = null;
    
    // Handle outbound segment
    if ((rebookingType === 'outbound' || rebookingType === 'entire') && 
        bookingStore.bookingData?.outbound_booking_tariff_segments?.[0]) {
      const outboundSegment = bookingStore.bookingData.outbound_booking_tariff_segments[0]
      
      if (outboundSegment.travel_date) {
        bookingOutboundDate = outboundSegment.travel_date
        
        // Only set the outbound date if user hasn't already selected one
        if (!journeyStore.departureDate) {
          journeyStore.setDepartureDate(bookingOutboundDate)
        }
      }
    }
    
    // Handle inbound segment
    if ((rebookingType === 'inbound' || rebookingType === 'entire') && 
        bookingStore.bookingData?.inbound_booking_tariff_segments?.[0]) {
      const inboundSegment = bookingStore.bookingData.inbound_booking_tariff_segments[0]
      
      if (inboundSegment.travel_date) {
        bookingReturnDate = inboundSegment.travel_date
        
        // Only set the inbound date if user hasn't already selected one
        if (!journeyStore.returnDate) {
          journeyStore.setReturnDate(bookingReturnDate)
          
          // Save to session storage to persist between page navigations
          sessionStorage.setItem('rebooking_return_date', bookingReturnDate)
        }
      }
    }
    
    // Update myTravelStore with the current dates (either user-selected or from booking data)
    // Use journeyStore as the single source of truth
    const finalOutboundDate = journeyStore.departureDate || myTravelStore.outboundDate || ''
    const finalReturnDate = journeyStore.returnDate || myTravelStore.returnDate || ''
    
    if (finalOutboundDate || finalReturnDate) {
      myTravelStore.setDates(finalOutboundDate, finalReturnDate)
    }
  }

  return {
    rebookingParams,
    bookingId,
    rebookingType,
    itemsToReplace,
    initializeRebookingFlow
  }
}
